import { Network } from "@aptos-labs/ts-sdk";
import { IconDeviceMobile } from "@decibeltrade/react-icons";
import { Button } from "@decibeltrade/web-core/components";
import { useTranslations } from "next-intl";
import { useCallback } from "react";
import { css } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { getSdkConfig } from "@/contexts/dex-sdk";
import { NetworkDownTooltip, useIsNetworkDown } from "@/contexts/health";
import { useDepositUsdcConditional } from "@/contexts/mutations/deposit-usdc-conditional";
import { useMintAndDepositUsdc } from "@/contexts/mutations/mint-and-deposit-usdc";
import { useAccountRestrictedMintAvailable } from "@/contexts/queries/account-restricted-mint-available-testnet";
import { useAccountBalanceQuery } from "@/contexts/queries/balances";
import { useDailyRestrictedMintsRemaining } from "@/contexts/queries/daily-restricted-mints-remaining-testnet";
import { useAccountTriggerResetMintTs } from "@/contexts/queries/trigger-account-reset-mint-ts-testnet";
import { useTriggerResetMintTs } from "@/contexts/queries/trigger-reset-mint-ts-testnet";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { useRequireTrading } from "@/hooks/use-require-trading";
import { useRequireTradingAllowed } from "@/hooks/use-require-trading-allowed";
import { useTransactionErrorToast } from "@/hooks/use-tx-error-toast";
import { formatDateTime } from "@/utils/format";
import { Logger } from "@/utils/logger";
import { chainUnitsToAmount } from "@/utils/math";

import { Clickable } from "../clickable";
import { MediaQuery } from "../media-query";
import { preloadDepositModal, useOpenDepositModal } from "../modal/deposit-modal";
import { openLinkMobileModal } from "../modal/link-mobile-modal";
import { preloadOnIntent } from "../modal/preload-on-intent";
import { preloadWithdrawModal, useOpenWithdrawModal } from "../modal/withdraw-modal";
import { Tooltip } from "../tooltip";

export interface AccountButtonsProps {
  accountAddress: string;
  isMobile?: boolean;
}

export const AccountButtons = ({ accountAddress, isMobile }: AccountButtonsProps) => {
  const requireTrading = useRequireTrading();
  const requireTradingAllowed = useRequireTradingAllowed();
  const isNetworkDown = useIsNetworkDown();
  const { subaccountAddress } = useWriteDexSdk();
  const { data: subaccountBalanceUSDC = 0 } = useAccountBalanceQuery();
  const t = useTranslations("accountControls");
  const openDepositModal = useOpenDepositModal();
  const openWithdrawModal = useOpenWithdrawModal({ subaccountAddress: subaccountAddress ?? "" });

  // Testnet - Daily and account mint limits are enabled.
  // Other networks - Daily and account mint limits are disabled.
  const config = getSdkConfig();
  const isMainnet = config.network === Network.MAINNET;
  const isTestnet = config.network === Network.TESTNET;

  const { data: mintsRemaining = 0, isPending: isPendingDailyMints } =
    useDailyRestrictedMintsRemaining({ enabled: isTestnet });
  const { data: availableMintChainUnits = 0, isPending: isPendingAccountMint } =
    useAccountRestrictedMintAvailable({
      accountAddr: accountAddress,
      enabled: isTestnet,
    });
  const { data: globalTriggerResetMintTs = 0 } = useTriggerResetMintTs({
    enabled: isTestnet,
  });
  const { data: accountTriggerResetMintTs = 0 } = useAccountTriggerResetMintTs({
    accountAddr: accountAddress,
    enabled: isTestnet,
  });

  const isPendingMintLimits = isTestnet && (isPendingDailyMints || isPendingAccountMint);
  const availableMintAmount = isTestnet ? chainUnitsToAmount(availableMintChainUnits) : 10_000; // Default to 10_000 USDC for non-testnet
  const isDailyLimitHit = isTestnet && mintsRemaining <= 1;
  const isAccountLimitHit = isTestnet && availableMintAmount === 0;
  const isRestrictedMintDisabled = isTestnet && (isDailyLimitHit || isAccountLimitHit);
  const isOver50k = !isTestnet && subaccountBalanceUSDC > 50_000;

  const { mintAndDeposit, mintAndDepositPending } = useMintFlow({
    subaccountAddress,
    subaccountBalanceUSDC,
    availableMintAmount,
  });

  const getMintTooltipContent = () => {
    if (isOver50k) {
      return t("over50kTooltip");
    }
    if (isAccountLimitHit) {
      const resetTimeString =
        accountTriggerResetMintTs > 0
          ? formatDateTime(accountTriggerResetMintTs * 1000, {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })
          : "";
      return resetTimeString
        ? t("mintDisabledAccountLimitDescription", { resetTimeString })
        : t("mintDisabledAccountLimit");
    }
    if (isDailyLimitHit) {
      const resetTimeString =
        globalTriggerResetMintTs > 0
          ? formatDateTime(globalTriggerResetMintTs * 1000, {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })
          : "";
      return resetTimeString
        ? t("mintDisabledDailyLimitDescription", { resetTimeString })
        : t("mintDisabledDailyLimit");
    }
  };

  return (
    <div className={flex({ align: "center", gap: "8" })}>
      {!isMainnet && (
        <Tooltip
          content={getMintTooltipContent()}
          placement="bottom"
          disabled={!isRestrictedMintDisabled && !isOver50k}
        >
          <Button
            fullWidth={isMobile}
            variant="secondary"
            disabled={isNetworkDown || isRestrictedMintDisabled || isOver50k || isPendingMintLimits}
            loading={mintAndDepositPending || isPendingMintLimits}
            onClick={requireTrading(mintAndDeposit)}
          >
            {t("mintUsdc")}
          </Button>
        </Tooltip>
      )}
      <NetworkDownTooltip isNetworkDown={isNetworkDown}>
        <Button
          fullWidth={isMobile}
          variant="secondary"
          disabled={isNetworkDown}
          onClick={openDepositModal}
          {...preloadOnIntent(preloadDepositModal)}
        >
          {t("deposit")}
        </Button>
      </NetworkDownTooltip>

      {!!subaccountAddress && (
        <NetworkDownTooltip isNetworkDown={isNetworkDown}>
          <Button
            fullWidth={isMobile}
            variant="secondary"
            disabled={isNetworkDown}
            // Withdrawals sign with the primary wallet, so no delegation gate —
            // MPC wallets (e.g. Fordefi) cannot delegate but must be able to exit.
            onClick={requireTradingAllowed(openWithdrawModal)}
            {...preloadOnIntent(preloadWithdrawModal)}
          >
            {t("withdraw")}
          </Button>
        </NetworkDownTooltip>
      )}
      {!isMobile && (
        <MediaQuery display="above" breakpoint="sm">
          {(className) => (
            <Tooltip content={t("linkMobileTooltip")} placement="bottom">
              <Clickable
                ariaLabel={t("linkMobile")}
                onClick={requireTrading(openLinkMobileModal)}
                className={className}
              >
                <IconDeviceMobile className={css({ h: "20", w: "20", color: "text.secondary" })} />
              </Clickable>
            </Tooltip>
          )}
        </MediaQuery>
      )}
    </div>
  );
};

export interface UseMintFlowArgs {
  subaccountAddress: string | undefined;
  subaccountBalanceUSDC: number;
  availableMintAmount: number;
}

export function useMintFlow({
  subaccountAddress,
  subaccountBalanceUSDC,
  availableMintAmount,
}: UseMintFlowArgs) {
  const t = useTranslations("accountControls");
  const showTransactionErrorToast = useTransactionErrorToast();

  const { mutateAsync: mintAndDepositUsdc, isPending: mintAndDepositPending } =
    useMintAndDepositUsdc();

  const mintAndDeposit = useCallback(
    async (args?: { onSuccess?: () => void }) => {
      if (!subaccountAddress) return;
      try {
        await mintAndDepositUsdc(availableMintAmount);
        args?.onSuccess?.();
      } catch (error) {
        showTransactionErrorToast(error, t("mintUsdcFailed"));
        Logger.error(error, {
          message: "Failed to mint and deposit USDC",
          amount: availableMintAmount,
          balance: subaccountBalanceUSDC,
          subaccountAddress,
        });
      }
    },
    [
      subaccountAddress,
      mintAndDepositUsdc,
      availableMintAmount,
      t,
      subaccountBalanceUSDC,
      showTransactionErrorToast,
    ],
  );

  return { mintAndDeposit, mintAndDepositPending };
}

export interface UseDepositFlowArgs {
  subaccountAddress: string | undefined;
  walletBalanceUSDC: number;
}

export function useDepositFlow({ subaccountAddress, walletBalanceUSDC }: UseDepositFlowArgs) {
  const t = useTranslations("accountControls");
  const showTransactionErrorToast = useTransactionErrorToast();

  const { mutateAsync: depositUsdcConditional, isPending: depositPending } =
    useDepositUsdcConditional();

  const deposit = useCallback(async () => {
    if (!subaccountAddress) return;
    if (walletBalanceUSDC === 0) return;
    try {
      await depositUsdcConditional({ amount: walletBalanceUSDC });
    } catch (error) {
      showTransactionErrorToast(error, t("depositUsdcFailed"));
      Logger.error(error, {
        message: "Failed to deposit USDC",
        amount: walletBalanceUSDC,
        subaccountAddress,
      });
    }
  }, [subaccountAddress, walletBalanceUSDC, depositUsdcConditional, showTransactionErrorToast, t]);

  return { deposit, depositPending };
}
